import { Fragment, useState, useMemo, useRef, useEffect } from "react";
import { ToolContainer } from "@/components/ToolLayout";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { ToolInfoSection } from "@/components/ToolInfoSection";
import { useLogToolUsage } from "@/hooks/useLogToolUsage";
import { withWordSegments, diffLines, splitLines, isIdentical, toSideBySide, type SideCell } from "./diffText";

const MAX_LINES = 3000; // guard against pathological input freezing the browser

const TOOL_INFO_CONTENT = {
  what: `Compares two blocks of text or code and highlights exactly what's different between them, line by line.`,
  how: `Paste your two versions of the text into the two panels, and the differences are highlighted automatically.`,
  tip: `This is useful well beyond code — comparing two drafts of a document, or two versions of a contract, works the same way and is often faster than manually reading both side by side.`,
};

export default function DiffChecker() {
  const [textA, setTextA] = useState("");
  const [textB, setTextB] = useState("");
  const [ignoreWhitespace, setIgnoreWhitespace] = useState(false);
  const [ignoreCase, setIgnoreCase] = useState(false);
  const [sideBySide, setSideBySide] = useState(false);
  const refA = useRef<HTMLTextAreaElement>(null);
  const refB = useRef<HTMLTextAreaElement>(null);

  // Hydration race: text typed or pasted into a box before React attached its
  // handlers never reached state, and the next re-render (for example from
  // filling the other box) wrote the empty state back over it. On mount, adopt
  // whatever is already in the boxes.
  useEffect(() => {
    if (refA.current && refA.current.value) setTextA(refA.current.value);
    if (refB.current && refB.current.value) setTextB(refB.current.value);
  }, []);

  const linesA = splitLines(textA);
  const linesB = splitLines(textB);
  const tooLarge = linesA.length > MAX_LINES || linesB.length > MAX_LINES;

  const diff = useMemo(() => {
    if (tooLarge || (!textA && !textB)) return [];
    const o = { ignoreWhitespace, ignoreCase };
    return withWordSegments(diffLines(linesA, linesB, o), o);
  }, [textA, textB, tooLarge, ignoreWhitespace, ignoreCase]);

  const additions = diff.filter(d => d.type === "add").length;
  const removals = diff.filter(d => d.type === "remove").length;
  const identical = isIdentical(diff);
  const rows = useMemo(() => (sideBySide ? toSideBySide(diff) : []), [diff, sideBySide]);
  const renderCell = (c: SideCell) => !c ? <div className="px-3 py-0.5 bg-muted/30">{"\u00a0"}</div> : (
    <div className={c.type === "add" ? "bg-green-50 text-green-900 px-3 py-0.5 break-words" : c.type === "remove" ? "bg-red-50 text-red-900 px-3 py-0.5 break-words" : "px-3 py-0.5 text-foreground/70 break-words"}>
      {c.segs ? c.segs.map((g, gi) => <span key={gi} className={g.changed ? (c.type === "add" ? "bg-green-300/70 rounded-sm" : "bg-red-300/70 rounded-sm") : undefined}>{g.text}</span>) : c.text || "\u00a0"}
    </div>
  );

  // Was useLogToolUsage(tooLarge) -- gated on the flag that means a
  // comparison did NOT happen, the opposite of "has a real result".
  // diff.length > 0 is the actual signal that a comparison ran.
  const { blocked } = useLogToolUsage(diff.length > 0);


  return (
    <ToolContainer>
      <div className="space-y-4">
        <div className="grid sm:grid-cols-2 gap-4">
          <div>
            <Label>Original Text</Label>
            <Textarea aria-label="Original Text" ref={refA} value={textA} onChange={e => setTextA(e.target.value)} className="min-h-[180px] font-mono text-xs" placeholder="Paste original text..." />
          </div>
          <div>
            <Label>Changed Text</Label>
            <Textarea aria-label="Changed Text" ref={refB} value={textB} onChange={e => setTextB(e.target.value)} className="min-h-[180px] font-mono text-xs" placeholder="Paste changed text..." />
          </div>
        </div>

        <div className="flex flex-wrap gap-4 text-sm">
          <label className="flex items-center gap-2"><input type="checkbox" checked={ignoreWhitespace} onChange={(e) => setIgnoreWhitespace(e.target.checked)} /> Ignore whitespace</label>
          <label className="flex items-center gap-2"><input type="checkbox" checked={sideBySide} onChange={(e) => setSideBySide(e.target.checked)} /> Side-by-side view</label>
          <label className="flex items-center gap-2"><input type="checkbox" checked={ignoreCase} onChange={(e) => setIgnoreCase(e.target.checked)} /> Ignore upper/lower case</label>
        </div>

        {tooLarge && (
          <p className="text-sm text-center text-muted-foreground p-4 border rounded-lg bg-muted/30">
            One of the inputs is very large ({MAX_LINES}+ lines) — comparison is skipped to keep your browser responsive. Try a smaller section.
          </p>
        )}

        {!tooLarge && !blocked && diff.length > 0 && (
          <div>
            {identical && <p className="mb-2 text-sm font-medium text-green-700 dark:text-green-400" role="status" data-testid="diff-identical">Texts are identical{ignoreWhitespace || ignoreCase ? " with the selected options" : ""}.</p>}
            <div className="flex gap-4 mb-2 text-xs">
              <span className="text-green-600 font-medium">+{additions} added</span>
              <span className="text-red-600 font-medium">-{removals} removed</span>
            </div>
            {sideBySide ? (
              <div className="rounded-lg border overflow-hidden font-mono text-xs grid grid-cols-2" data-testid="diff-side-by-side">
                {rows.map((r, i) => <Fragment key={i}><div className="border-r">{renderCell(r.left)}</div><div>{renderCell(r.right)}</div></Fragment>)}
              </div>
            ) : (
            <div className="rounded-lg border overflow-hidden font-mono text-xs">
              {diff.map((line, i) => (
                <div
                  key={i}
                  className={
                    line.type === "add"
                      ? "bg-green-50 text-green-900 px-3 py-0.5"
                      : line.type === "remove"
                      ? "bg-red-50 text-red-900 px-3 py-0.5"
                      : "px-3 py-0.5 text-foreground/70"
                  }
                >
                  <span className="select-none mr-2 text-muted-foreground">
                    {line.type === "add" ? "+" : line.type === "remove" ? "-" : " "}
                  </span>
                  {line.segs
                    ? line.segs.map((g, gi) => (
                        <span key={gi} className={g.changed ? (line.type === "add" ? "bg-green-300/70 rounded-sm" : "bg-red-300/70 rounded-sm") : undefined}>{g.text}</span>
                      ))
                    : line.text || "\u00a0"}
                </div>
              ))}
            </div>
            )}
          </div>
        )}
      </div>
          <ToolInfoSection whatIsIt={TOOL_INFO_CONTENT.what} howToUse={TOOL_INFO_CONTENT.how} tip={TOOL_INFO_CONTENT.tip} />
    </ToolContainer>
  );
}
